<script lang="ts">
  import type { Snippet } from 'svelte';
  import { type StampFromSet, type Zhihao } from '@briefmarken_katalog/types';

  import Title from './Title.svelte';
  import FaceValue from './FaceValue.svelte';
  import FieldSeparator from './FieldSeparator.svelte';
  import Picture from '../Picture.svelte';
  import RowActionButtons from './RowActionButtons.svelte';
  import CatalogDetailDialog from './CatalogDetailDialog.svelte';

  import StampInSetEditor from '../editors/StampInSetEditor.svelte';
  import { StampInSetModel } from '../editors/models/StampInSetModel.svelte';
  import { user } from "$lib/stores/auth";
  import { catalogMode } from "$lib/stores/ui";

  import './row.css'

  import { type ImageType } from '@briefmarken_katalog/types';
  type Props = {
    stamp: StampFromSet;
    readonly?: boolean;
    onEdit?: (stamp: StampFromSet) => void;
    fetchFullImage?: () => Promise<ImageType | undefined>;
    /** Issue date of the containing set — a stamp of a set has none of its own */
    dateOfIssue?: Date;
    /** Zhihao of the containing set — a stamp of a set only varies in its position */
    parentZhihao?: Zhihao;
    /** 1-based slot in the set; together with parentZhihao it yields the stamp's Zhihao */
    positionInSet?: number;
    /** Resolves the stamp with its full-size images, for the catalog detail dialog */
    fetchFullStamp?: () => Promise<StampFromSet | undefined>;
    leading?: Snippet;
  };

  const { stamp, readonly = false, onEdit, fetchFullImage, dateOfIssue, parentZhihao, positionInSet, fetchFullStamp, leading }: Props = $props();

  let isViewerOpen: boolean = $state(false);
  let editorModel = $state<StampInSetModel | null>(null);
  let catalogDetailStamp = $state<StampFromSet | null>(null);
  let isCatalogDetailOpen = $state(false);

  async function openDetails(ro: boolean) {
    const isReadonly = !onEdit || ro;
    // Catalog mode gets the presentation-style detail dialog instead of the editor
    if (isReadonly && $catalogMode) {
      catalogDetailStamp = (fetchFullStamp ? await fetchFullStamp() : undefined) ?? stamp;
      isCatalogDetailOpen = true;
      return;
    }
    editorModel = isReadonly
      ? new StampInSetModel({ isReadonly: true, initialValue: stamp, parentZhihao, positionInSet })
      : new StampInSetModel({ isReadonly: false, initialValue: stamp, parentZhihao, positionInSet, onCreate: onEdit!, submitLabel: 'editor.save' });
    isViewerOpen = true;
  }
</script>

<!-- Desktop: single flex line (wrappers dissolve via sm:contents / sm:order-*); mobile: stacked lines -->
<div class="row bgr-col max-sm:flex-col max-sm:gap-1">
  {@render leading?.()}
  <!-- mobile line 1: picture + title -->
  <div class="max-sm:flex max-sm:items-start max-sm:gap-1 max-sm:w-full sm:contents">
    <div class="shrink-0 sm:order-3">
      <Picture picture={stamp.pictures?.[0]} {fetchFullImage} />
    </div>
    <div class="min-w-0 flex-1 sm:order-5">
      <Title text={stamp.title}/>
    </div>
  </div>
  <!-- mobile line 2: face value · actions -->
  <div class="max-sm:flex max-sm:items-center max-sm:w-full sm:contents">
    <div class="sm:order-1">
      <FaceValue data={stamp.face_value}/>
    </div>
    <div class="max-sm:ml-auto sm:order-7">
      <RowActionButtons
        {readonly}
        state='Suggested'
        reviewedBy={null}
        currentUserId={$user?.id}
        userCanReview={false}
        userCanEdit={!!$user && !!onEdit}
        userIsAdmin={false}
        userIsLoggedIn={!!$user}
        onReview={() => {}}
        onDetails={openDetails}
      />
    </div>
  </div>
  <!-- desktop-only vertical dividers -->
  <FieldSeparator class="sm:order-2"/>
  <FieldSeparator class="sm:order-4"/>
  <FieldSeparator class="sm:order-6"/>
</div>

{#if isViewerOpen && editorModel}
<StampInSetEditor bind:model={editorModel!} bind:isOpen={isViewerOpen}/>
{/if}

{#if isCatalogDetailOpen && catalogDetailStamp}
<CatalogDetailDialog item={catalogDetailStamp} {dateOfIssue} bind:isOpen={isCatalogDetailOpen} />
{/if}
